import React from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle2,
  Hourglass,
  RefreshCw,
  OctagonX,
} from 'lucide-react';

import InfoTooltip from 'components/InfoTooltip';
import { formatDate } from 'utils/format';

import './styles.scss';

const StatementsTable = ({
  statements,
  isLoading,
}) => {

  const renderStatusBadge = (statement) => {
    if (statement.isDeleted) {
      return <span className="confidence-badge bg-red-500 !py-1 !px-2 !rounded text-white cursor-not-allowed">Deleted</span>;
    }

    if (statement.processingStatus === 'Failed') {
      return (
        <InfoTooltip
          className="w-full justify-center md:justify-start"
          tooltip="Invalid upload"
          mobileTooltip={
            <div className="flex flex-col items-center justify-center gap-4 pb-4">
              <OctagonX className="text-red-500 w-12 h-12" />
              <span className="text-base text-center">Invalid upload</span>
            </div>
          }
        >
          <span
            className="confidence-badge review cursor-pointer"
          >
            <OctagonX className="w-6 h-6 md:w-4 md:h-4 md:mr-1" />
            <span className="hidden md:block">Failed</span>
          </span>
        </InfoTooltip>
      );
    }

    if (statement.processingStatus === 'InProgress') {
      return (
        <span
          className="confidence-badge cursor-not-allowed processing"
          title="Processing in progress."
        >
          <RefreshCw className="w-7 h-7 p-1 md:p-0 md:w-4 md:h-4 md:mr-1 animate-spin" />
          <span className="hidden md:block">Processing...</span>
        </span>
      );
    }

    return !statement.isReconciled ? (
      <InfoTooltip
        tooltip="This statement will be manually reviewed"
        mobileTooltip={
          <div className="flex flex-col items-center justify-center gap-4 pb-4">
            <Hourglass className="text-red-500 w-12 h-12" />
            <span className="text-base text-center">This statement will be manually reviewed</span>
          </div>
        }
      >
        <span className="confidence-badge in-review">
          <Hourglass className="w-8 h-8 p-1 md:p-0 md:w-4 md:h-4 md:mr-1" />
          <span className="hidden md:block">In Review</span>
        </span>
      </InfoTooltip>
    ) : (
      <div className="relative inline-flex items-center justify-center md:justify-start w-full">
        <span
          className="confidence-badge verified"
          title="Looks Great! Bank Statement Uploaded Successfully."
        >
          <CheckCircle2 className="w-6 h-6 md:w-4 md:h-4 md:mr-1" />
          <span className="hidden md:block">OK</span>
        </span>
      </div>
    );
  };

  return (
    <div className="table-wrapper">
      <table className="statement-table">
        <thead>
          <tr>
            <th>Bank Name</th>
            <th>Statement Period</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={5} className="text-center py-8">
                <div className="mx-auto mt-2 w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="mt-2 text-gray-600">
                  Loading statements...
                </p>
              </td>
            </tr>
          ) : statements.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-8">
                <p className="text-gray-600">No statements found</p>
              </td>
            </tr>
          ) : (
            statements
              .sort((a, b) => {
                const dateA = new Date(a.statementEndDate || '');
                const dateB = new Date(b.statementEndDate || '');
                return dateA.getTime() - dateB.getTime();
              })
              .map(statement => (
                <tr
                  key={statement.bankStatementId}
                  className={statement.isDeleted ? 'opacity-50' : ''}
                >
                  <td>
                    {statement.processingStatus === 'Done' ? (statement.bankName || (<span className="text-red-500 italic">Bank Name not captured</span>)) : (statement.bankName || statement.originalFileName || 'N/A')}
                  </td>
                  <td>
                    {!(statement.statementStartDate && statement.statementEndDate) ? 'N/A' : `${formatDate(statement.statementStartDate)} to ${formatDate(statement.statementEndDate)}`}
                  </td>
                  <td>
                    {renderStatusBadge(statement)}
                  </td>
                </tr>
              ))
          )}
        </tbody>
      </table>
    </div>
  );
};

StatementsTable.propTypes = {
  statements: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default StatementsTable;
